/* ═══════════════════════════════════════════════════════════════════════════
   WHAT THE DAY TOOK

   Revenue per day, and per person. Two numbers a business looks at before it
   looks at anything else, and neither existed.

   ── The distinction the whole file turns on ───────────────────────────────

   An appointment carries an amount long before anybody is charged. The money
   moves through three states — see domain/authorisation.js — and calling all
   three "revenue" is how a business ends up reporting a figure it never
   received:

     AGREED     the price on the booking. A number both sides said yes to,
                and nothing more. An appointment next Thursday is agreed.
     HELD       authorised on the card or in Vipps and reserved. Still the
                customer's money.
     TAKEN      captured. This is revenue, and it is the only one of the
                three an accountant may have.

   So the three are counted separately and never summed into one figure. A
   screen may lead with whichever it likes; it may not add them up.

   In this build nothing is ever captured — there are no merchant keys, which
   every screen already says. That makes TAKEN zero and AGREED the interesting
   number, and it makes saying which is which more important rather than less:
   the day somebody wires up Stripe, the same figures start meaning different
   things, and code that had blurred them would quietly start lying.

   ── What is not counted ───────────────────────────────────────────────────

   A cancelled appointment, and the demonstration day. The demo rows exist so
   an empty browser shows a working diary; a business that opened its takings
   and found the platform's sample day in them would be right to distrust
   every other number on the screen.

   ── And no VAT ───────────────────────────────────────────────────────────

   Not here. Norway charges 25 % on most services, 15 % on food, and some
   health work is exempt entirely — which of those applies is the business's
   own fact and not something to derive from a trade code. The receipt splits
   it where the rate is known (domain/receipt.js, a restaurant bill). Here the
   figures are what they are, and the accounting export is where the rate has
   to be stated rather than guessed.
   ═══════════════════════════════════════════════════════════════════════ */

/** The three states, in the order money passes through them. */
export const STATES = ['agreed', 'held', 'taken'];

export const STATE_WORDS = {
  agreed: { label: 'Agreed', what: 'Booked and priced. Nobody has been charged.' },
  held: { label: 'Held', what: 'Authorised and reserved. Still the customer\'s money.' },
  taken: { label: 'Taken', what: 'Captured. This is the only one an accountant may have.' },
};

const num = n => (Number.isFinite(Number(n)) ? Number(n) : 0);

/** The date part of a row's time, or ''. */
export const dayOf = row => String(row?.time || '').slice(0, 10);

/**
 * Does this row count at all?
 *
 * The demonstration day never does, whatever it says it is worth, and a
 * cancelled appointment never does either.
 */
export function counts(row) {
  if (!row) return false;
  if (row.source && row.source !== 'avtale.net') return false;
  if (row.demo) return false;
  return row.status !== 'cancelled';
}

/**
 * One row's money, in the three states.
 *
 * A row contributes to exactly one of `held` and `taken` — the hold is either
 * still reserved or it has been captured, never both — while `agreed` counts
 * every row that got as far as a price.
 */
export function moneyOf(row) {
  const out = { agreed: 0, held: 0, taken: 0 };
  if (!counts(row)) return out;
  const auth = row.auth || null;
  out.agreed = num(row.amount ?? auth?.amount);
  if (auth?.state === 'reserved') out.held = num(auth.amount);
  if (auth?.state === 'captured') out.taken = num(auth.charged ?? auth.amount);
  return out;
}

/** Add up a set of rows. `count` is appointments, not kroner. */
export function takings(rows = []) {
  const live = rows.filter(counts);
  const out = { agreed: 0, held: 0, taken: 0, count: live.length };
  live.forEach(r => {
    const m = moneyOf(r);
    out.agreed += m.agreed;
    out.held += m.held;
    out.taken += m.taken;
  });
  return out;
}

/**
 * Per day, oldest first, with the empty days kept.
 *
 * A day with nothing in it is a fact about the business — Sundays, the week
 * the shop was shut — and dropping it turns a line on a chart into a lie
 * about which days those figures belong to.
 */
export function byDay(rows = [], { from, to } = {}) {
  const days = [];
  const seen = {};
  rows.filter(counts).forEach(r => {
    const d = dayOf(r);
    if (!d) return;
    (seen[d] = seen[d] || []).push(r);
  });

  if (from && to) {
    const a = new Date(from + 'T12:00:00');
    const b = new Date(to + 'T12:00:00');
    for (let d = new Date(a); d <= b; d.setDate(d.getDate() + 1)) {
      const iso = d.getFullYear() + '-'
        + String(d.getMonth() + 1).padStart(2, '0') + '-'
        + String(d.getDate()).padStart(2, '0');
      days.push({ date: iso, ...takings(seen[iso] || []) });
    }
    return days;
  }
  return Object.keys(seen).sort().map(d => ({ date: d, ...takings(seen[d]) }));
}

/**
 * Per person.
 *
 * The roster leads, so somebody who did nothing this week appears with zero
 * rather than vanishing — an absence is the thing a manager is looking for.
 * Work that names nobody is gathered under its own row instead of being
 * dealt out to whoever happens to be first: a trade where the firm allocates
 * books no person at all (see `picksPerson`), and attributing that revenue
 * to an individual would be inventing a fact about their week.
 */
export function byPerson(rows = [], roster = []) {
  const live = rows.filter(counts);
  const out = roster.map(p => ({
    id: p.id || null,
    name: p.name,
    ...takings(live.filter(r => (r.staffId && p.id ? r.staffId === p.id : r.staff === p.name))),
  }));
  const named = new Set(roster.map(p => p.id).filter(Boolean));
  const namedNames = new Set(roster.map(p => p.name));
  const loose = live.filter(r =>
    !(r.staffId && named.has(r.staffId)) && !namedNames.has(r.staff));
  if (loose.length) {
    out.push({ id: null, name: 'Not assigned to a person', loose: true, ...takings(loose) });
  }
  return out;
}

/**
 * How one figure compares with another, as a percentage.
 *
 * Null rather than a number when the earlier figure is zero: everything is an
 * infinite rise on nothing, and "+∞ %" on a dashboard is noise where a blank
 * is information.
 */
export function change(now, before) {
  const a = num(now);
  const b = num(before);
  if (!b) return null;
  return Math.round(((a - b) / b) * 100);
}

/** What the screen has to say about the figures, in this build. */
export const takingsNote = () =>
  'No merchant keys are configured, so nothing has been captured. These are '
  + 'the amounts agreed on the bookings, not money that has moved.';
